import { Space } from "antd";
import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ApiService } from "../../api";
import DynamicFilterForm, {
  FilterValues,
} from "../../components/DynamicFilterForm";
import { exportRequestsToExcel } from "../../utils/exportRequests";
import ManageableTable, {
  ManageableColumn,
} from "../../components/ManageableTable";
import { useRefreshOnTabFocus, useTabTitle } from "../../tabs/tabContext";
import { usePagination } from "../../hooks/usePagination";
import { PageHeader, useHeaderSlot } from "../../components/PageHeader";

const RequestListPage = () => {
  const { t } = useTranslation();
  const { shortName } = useParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [reload, setReload] = useState<number>(0);
  const [searchFields, setSearchFields] = useState<any[]>([]);
  const [requests, setRequests] = useState<any[]>([]);
  const [statuses, setStatuses] = useState<any[]>([]);
  // A drill-down from Analytics ("12 cases waiting at Approval") arrives as
  // ?status=<id>. Read once, on mount: the URL seeds the list, it does not own
  // it, so filtering from the panel afterwards must not be undone by the query
  // string that is still in the address bar.
  const [searchParams] = useSearchParams();
  const initialFilters = useMemo<FilterValues>(() => {
    const status = searchParams.get("status");
    return status ? { status: [Number(status)] } : {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [filterValues, setFilterValues] =
    useState<FilterValues>(initialFilters);
  const [templateName, setTemplateName] = useState<string>("");
  const { page, pageSize, total, applyResponse, resetPage, paginationProps } =
    usePagination();
  const { slot, target } = useHeaderSlot();
  const filtered = Object.keys(filterValues).length > 0;
  const navigate = useNavigate();

  // Name the tab after the template, e.g. "Hot Work Permit - Requests".
  useTabTitle(templateName ? `${templateName} - ${t("Requests")}` : undefined);

  // Fetch the template's display name (in parallel; doesn't block the list).
  useEffect(() => {
    (async () => {
      try {
        const tpl = await ApiService.apiMainTemplatesRetrieve(shortName!);
        setTemplateName(tpl.name);
      } catch {
        /* ignore — keep the generic title */
      }
    })();
  }, [shortName]);

  useEffect(() => {
    (async () => {
      setLoading(true);

      const result = await ApiService.apiMainSearchFieldsByTemplateRetrieve(
        shortName!
      );
      setSearchFields(result);

      const statuses =
        await ApiService.apiMainRequestStatusesByShortNameRetrieve(shortName!);
      setStatuses(statuses);

      setLoading(false);
    })();
  }, [shortName]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const result: any = await ApiService.apiMainRequestsByTemplateCreate({
          template_short_name: shortName!,
          filter: filterValues,
          page,
          page_size: pageSize,
        });
        setRequests(applyResponse<any>(result));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortName, filterValues, page, pageSize, reload]);

  // Statuses change from the request tab (approve, reject, close); refresh when
  // this list comes back to the foreground rather than showing yesterday's.
  useRefreshOnTabFocus(() => setReload((n) => n + 1));

  const columns: ManageableColumn<any>[] = useMemo(
    () => [
      {
        key: "request_id",
        title: t("ID"),
        dataIndex: "request_id",
        width: 90,
      },
      ...searchFields.map((x) => ({
        key: x.field_name,
        title: x.title,
        dataIndex: x.field_name,
        width: 180,
      })),
      {
        key: "actions",
        title: t("Actions"),
        width: 160,
        hideable: false,
        render: (_: any, record: any) => (
          <Space>
            <Space size="middle">
              <a href={`/requests/${record.request_id}`}>{t("View")}</a>
            </Space>
            <Space size="middle">
              <a
                onClick={() =>
                  navigate(`/requests/${shortName}/create`, {
                    state: { requestId: record.request_id },
                  })
                }
              >
                {t("Duplicate")}
              </a>
            </Space>
          </Space>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchFields, shortName]
  );

  return (
    <div>
      {/* The template's own name once it arrives — the tab is already named
          after it, and "Request list" is the same title on eight pages. */}
      <PageHeader
        title={templateName || t("Request list")}
        meta={
          filtered
            ? t("{{count}} matching the filter", { count: total })
            : t("{{count}} in total", { count: total })
        }
        actions={slot}
      />
      <DynamicFilterForm
        filters={searchFields}
        statuses={statuses}
        initialValues={initialFilters}
        onFilter={(values) => {
          // Reset here, not in an effect — batching keeps this to one fetch.
          setFilterValues(values);
          resetPage();
        }}
        onExport={() =>
          exportRequestsToExcel(
            { template_short_name: shortName, filter: filterValues },
            `${shortName}_requests.xlsx`
          )
        }
      />
      <ManageableTable
        tableId={`requests-template:${shortName}`}
        toolbarTarget={target}
        bordered
        loading={loading}
        dataSource={requests}
        columns={columns}
        pagination={paginationProps}
        rowKey={(r: any) => r.request_id}
      />
    </div>
  );
};

export default RequestListPage;
